import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

// DEFECT-D-19/D-22: replaces the plain 4-tile status count with an actual chart + working
// drilldown — clicking any slice (or its legend row) jumps straight to the Gyms list
// pre-filtered to that exact status. Colors follow UI_STANDARDS.md's B0 rule: --scarlet-accent
// (brand red) is reserved for primary/selected UI only, never used here to mean "bad" — status
// semantics use the dedicated --scarlet-pos/--scarlet-neg tokens plus a neutral amber for the
// two "in between" states.
const STATUS_META = {
  active: { label: "Active", color: "var(--scarlet-pos)" },
  trial: { label: "On trial", color: "#94A3B8" },
  grace: { label: "Grace period", color: "#D97706" },
  read_only: { label: "Suspended (read-only)", color: "#D97706" },
  suspended: { label: "Suspended (blocked)", color: "var(--scarlet-neg)" },
  deleted: { label: "Deleted", color: "#6B6B72" },
};

export function GymsByStatusChart({ byStatus, loading }) {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  if (loading || !byStatus) {
    return <div className="solid-surface rounded-[14px] p-5 h-72 animate-pulse" data-testid={CONTROL.gymsByStatusChart} />;
  }
  const data = Object.entries(byStatus).filter(([, v]) => v > 0).map(([key, value]) => ({
    key, value, label: STATUS_META[key]?.label || key, color: STATUS_META[key]?.color || "#94A3B8",
  }));
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <motion.div {...fadeUp(reducedMotion)} className="solid-surface rounded-[14px] p-5" data-testid={CONTROL.gymsByStatusChart}>
      <p className="eyebrow mb-3">Gyms by status</p>
      {total === 0 ? (
        <p className="text-sm py-10 text-center" style={{ color: "var(--scarlet-text-secondary)" }}>No gyms provisioned yet.</p>
      ) : (
        <div className="flex items-center gap-4">
          <div className="w-32 h-32 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="label" innerRadius={38} outerRadius={58} paddingAngle={2} isAnimationActive={!reducedMotion}>
                  {data.map((d) => <Cell key={d.key} fill={d.color} className="cursor-pointer" onClick={() => navigate(`/kairoo/gyms?status=${d.key}`)} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} gym${v === 1 ? "" : "s"}`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-1.5">
            {data.map((d) => (
              <button
                key={d.key} data-testid={CONTROL.kpiCard(`status-${d.key}`)}
                onClick={() => navigate(`/kairoo/gyms?status=${d.key}`)}
                className="w-full flex items-center justify-between gap-2 text-left px-2 py-1 rounded-[8px] hover:bg-black/[0.03] transition-colors scarlet-focusable"
              >
                <span className="text-sm flex items-center gap-2" style={{ color: "var(--scarlet-text)" }}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                  {d.label}
                </span>
                <span className="text-sm font-bold tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{d.value}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
